import React, { useState, useEffect } from 'react';
import { ChevronDown, Paperclip } from 'lucide-react';
import { toast } from 'react-toastify';
import ConfirmModal from '../../../UI/Modal/confirmModal';
import { useUserContext } from '../../../../contexts/UserContext';
import { permission } from '../../../../config/permission';
import { uploadEpmTestAttachments } from '../../../../utils/APIs';
import { notifyAutoSaved } from '../useAutoSave';
import { variantLetter, isImageFile, toVariantPayload } from './utils';
import { EPM_LIMITS } from '../../validation/epmRules';
import { variantDetailsSchema } from '../../validation/epmSchemas';
import EpmCharCounter from '../../EpmCharCounter';
import { Input } from '../../../UI';

const VariantsSection = ({
    testData,
    setTestData,
    testId,
    mockups,
    hasShots,
    updateTest,
    userData,
}) => {
    const { checkPermission } = useUserContext();
    const canUpdate = checkPermission(permission.XCM_TESTS_UPDATE);
    const savedVariants = testData?.build_qa?.variants || [];
    const [allocationOpen, setAllocationOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [newVariantFile, setNewVariantFile] = useState(null);
    const [newVariantPreview, setNewVariantPreview] = useState('');
    const [newVariantDetails, setNewVariantDetails] = useState('');
    const [isSavingVariant, setIsSavingVariant] = useState(false);
    const [deleteVariantIndex, setDeleteVariantIndex] = useState(null);
    const [isDeletingVariant, setIsDeletingVariant] = useState(false);

    useEffect(() => () => {
        if (newVariantPreview.startsWith('blob:')) URL.revokeObjectURL(newVariantPreview);
    }, [newVariantPreview]);

    const editingVariant = editingIndex !== null ? savedVariants[editingIndex] : null;
    const canSaveVariant = !isSavingVariant && !!newVariantDetails.trim() && (!!newVariantFile || !!editingVariant);

    const resetVariantForm = () => {
        setEditingIndex(null);
        setNewVariantFile(null);
        setNewVariantPreview('');
        setNewVariantDetails('');
    };

    const toggleAllocation = () => {
        if (allocationOpen) resetVariantForm();
        setAllocationOpen(!allocationOpen);
    };

    const handleVariantFile = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        setNewVariantFile(file);
        setNewVariantPreview(file.type.startsWith('image/') ? URL.createObjectURL(file) : '');
    };

    const startEditVariant = (index) => {
        const variant = savedVariants[index];
        setNewVariantFile(null);
        setNewVariantPreview(isImageFile(variant) ? variant.source_url : '');
        setNewVariantDetails(variant.details || '');
        setEditingIndex(index);
        setAllocationOpen(true);
    };

    // Sends the full variant list and keeps the saved (re-signed) list on the test
    const saveVariants = async (variants) => {
        if (!canUpdate) return;
        const res = await updateTest(testId, { build_qa: { variants: variants.map(toVariantPayload) } });
        const saved = res?.build_qa?.variants || variants;
        setTestData((prev) => ({ ...prev, build_qa: { ...prev?.build_qa, variants: saved } }));
        notifyAutoSaved();
    };

    const handleSaveVariant = async (event) => {
        event.stopPropagation();
        if (!canSaveVariant || !testId) return;

        if (!editingVariant && savedVariants.length >= EPM_LIMITS.VARIANTS_MAX_COUNT) {
            toast.error(`Maximum ${EPM_LIMITS.VARIANTS_MAX_COUNT} variants allowed.`);
            return;
        }

        try {
            variantDetailsSchema.validateSync(newVariantDetails);
        } catch (err) {
            toast.error(err.message || `Variant details must be at most ${EPM_LIMITS.VARIANT_DETAILS_MAX} characters.`);
            return;
        }

        setIsSavingVariant(true);
        try {
            let file = editingVariant ? { source_url: editingVariant.source_url, key: editingVariant.key } : null;
            if (newVariantFile) {
                const formData = new FormData();
                formData.append('test_id', testId);
                formData.append('folder', 'variants');
                formData.append('files', newVariantFile);
                const res = await uploadEpmTestAttachments(
                    userData?.token,
                    userData?.selectedOrganization?.org_token,
                    formData
                );
                const uploaded = res?.data?.[0];
                if (!uploaded?.url) throw new Error("Couldn't upload the variant file");
                file = { source_url: uploaded.url, key: uploaded.key };
            }
            const variant = {
                ...(editingVariant?._id && { _id: editingVariant._id }),
                ...file,
                details: newVariantDetails.trim(),
            };
            const variants = editingVariant
                ? savedVariants.map((item, index) => (index === editingIndex ? variant : item))
                : [...savedVariants, variant];
            await saveVariants(variants);
            resetVariantForm();
            setAllocationOpen(false);
        } catch (err) {
            console.error('Failed to save variant:', err);
            toast.error(err?.message || "Couldn't save the variant");
        } finally {
            setIsSavingVariant(false);
        }
    };

    const handleDeleteVariant = async () => {
        if (deleteVariantIndex === null) return;
        setIsDeletingVariant(true);
        try {
            await saveVariants(savedVariants.filter((_, index) => index !== deleteVariantIndex));
            if (editingIndex === deleteVariantIndex) {
                resetVariantForm();
                setAllocationOpen(false);
            }
            setDeleteVariantIndex(null);
        } catch (err) {
            console.error('Failed to delete variant:', err);
            toast.error(err?.message || "Couldn't delete the variant");
        } finally {
            setIsDeletingVariant(false);
        }
    };

    return (
        <>
            <section className="epm-plan__card mb-0" id="epm-field-variants">
                <div className="epm-plan__section-heading mb-[14px]">
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold tracking-[.08em] uppercase text-[#8d96ac]">Variants</span>
                        <span className="text-[11px] text-[#8992a8]">
                            ({savedVariants.length}/{EPM_LIMITS.VARIANTS_MAX_COUNT})
                        </span>
                    </div>
                    {hasShots && (
                        <span className="text-[11px] text-[#8992a8] ml-2">
                            {mockups?.device || 'Device not specified'} · captured on {mockups?.site || 'Site not specified'}
                        </span>
                    )}
                </div>

                {savedVariants.map((variant, index) => (
                    <div key={variant._id || variant.key || index} className="epm-build__vr">
                        <a
                            href={variant.source_url}
                            target="_blank"
                            rel="noreferrer"
                            className={`epm-build__shot ${isImageFile(variant) ? 'overflow-hidden p-0' : ''}`}
                            title="Open file"
                        >
                            {isImageFile(variant) ? (
                                <img
                                    src={variant.source_url}
                                    alt={`Variant ${variantLetter(index)}`}
                                    className="w-full h-full object-cover object-top"
                                />
                            ) : (
                                <span className="flex flex-col items-center gap-1">
                                    <Paperclip size={14} />file
                                </span>
                            )}
                        </a>
                        <div className="epm-build__vname">
                            <div className="text-[13px] text-[#334064]">
                                <strong>{variantLetter(index)}</strong>
                            </div>
                            <div className="flex items-center gap-3 relative">
                                <Input
                                    size="sm"
                                    type="text"
                                    value={variant.details || ''}
                                    disabled
                                    aria-label={`Variant ${variantLetter(index)} details`}
                                    className="flex-1 min-w-0"
                                />
                            </div>
                            {canUpdate && (
                                <div className="flex items-center gap-3 text-[11px]">
                                    <button
                                        type="button"
                                        onClick={() => startEditVariant(index)}
                                        className="underline text-[#18181b] bg-transparent border-0 p-0 cursor-pointer"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setDeleteVariantIndex(index)}
                                        className="underline text-[#a33c49] bg-transparent border-0 p-0 cursor-pointer"
                                    >
                                        Delete
                                    </button>
                                </div>
                            )}
                        </div>
                        <span className="epm-build__pct"></span>
                    </div>
                ))}

                {canUpdate && (
                    savedVariants.length < EPM_LIMITS.VARIANTS_MAX_COUNT || editingIndex !== null ? (
                        <div className="epm-plan__collapsible flat mt-2">
                            <div
                                className={`epm-plan__collapsible-header relative ${allocationOpen ? 'is-open' : ''}`}
                                onClick={toggleAllocation}
                            >
                                <ChevronDown size={15} />
                                <span className="epm-plan__collapsible-title text-xs">
                                    {editingIndex !== null ? `Edit ${variantLetter(editingIndex)}` : 'Add new variant'}
                                </span>
                                {allocationOpen && (
                                    <button
                                        type="button"
                                        onClick={handleSaveVariant}
                                        disabled={!canSaveVariant}
                                        className="ml-auto px-3 py-1.5 border border-[var(--primary)] rounded-md bg-[var(--primary)] text-[11px] text-white font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSavingVariant ? 'Saving...' : 'Save'}
                                    </button>
                                )}
                            </div>
                            {allocationOpen && (
                                <div className="epm-plan__collapsible-body flex flex-col gap-3">
                                    <div className="epm-build__vr">
                                        <label
                                            className={`epm-build__shot ${newVariantPreview ? 'overflow-hidden p-0' : 'drop'} cursor-pointer`}
                                        >
                                            {newVariantPreview ? (
                                                <img
                                                    src={newVariantPreview}
                                                    alt="Variant file"
                                                    className="w-full h-full object-cover object-top"
                                                />
                                            ) : newVariantFile ? (
                                                <span className="break-all">{newVariantFile.name}</span>
                                            ) : editingVariant ? (
                                                'Replace file'
                                            ) : (
                                                'Upload file'
                                            )}
                                            <input
                                                type="file"
                                                className="hidden"
                                                onChange={handleVariantFile}
                                                disabled={isSavingVariant}
                                                aria-label="Variant file"
                                            />
                                        </label>
                                        <div className="epm-build__vname">
                                            <div className="flex items-center justify-between text-[13px] text-[#334064] mb-1">
                                                <strong>{editingVariant ? `Edit ${variantLetter(editingIndex)}` : 'New variant'}</strong>
                                                <EpmCharCounter current={newVariantDetails} max={EPM_LIMITS.VARIANT_DETAILS_MAX} />
                                            </div>
                                            <Input
                                                size="sm"
                                                type="text"
                                                maxLength={EPM_LIMITS.VARIANT_DETAILS_MAX}
                                                value={newVariantDetails}
                                                onChange={(e) => setNewVariantDetails(e.target.value)}
                                                disabled={isSavingVariant}
                                                placeholder="Variant details"
                                                aria-label="Variant details"
                                            />
                                        </div>
                                        <span className="epm-build__pct"></span>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 font-medium mt-2">
                            Maximum {EPM_LIMITS.VARIANTS_MAX_COUNT} variants reached.
                        </div>
                    )
                )}
            </section>

            {deleteVariantIndex !== null && (
                <ConfirmModal
                    title="Delete Variant"
                    description={`Are you sure you want to delete variant ${variantLetter(deleteVariantIndex)}?`}
                    ConfirmText="Delete"
                    loading={isDeletingVariant}
                    hideModal={() => setDeleteVariantIndex(null)}
                    handleConfirmation={handleDeleteVariant}
                />
            )}
        </>
    );
};

export default VariantsSection;

